import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { LoaderCircle, Search } from "lucide-react";

import { cn } from "utils";
import { AlertContext, JobsContext } from "context/providers";
import {
  GET_JOB_OPPORTUNITY_ASSESSMENTS,
  SEARCH_ROPES_PROBLEMS,
} from "api/graphql/jobOpportunity";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import Paginator from "components/Paginator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { DialogFooter } from "components/ui/dialog";

const formatStatus = (status) => {
  if (!status) {
    return "Unknown";
  }

  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const StatusIndicator = ({ status }) => (
  <span className="inline-flex items-center gap-x-2">
    <span
      className={cn(
        "inline-block h-2.5 w-2.5 rounded-full",
        status === "READY" ? "bg-green-600" : "bg-yellow-500"
      )}
    />
    {formatStatus(status)}
  </span>
);

// Renders an assessment's title (optionally as a preview link) plus a subtitle
// when the assessment comes from the Ropes Test Bank / Assessments Library.
const AssessmentTitle = ({ label, previewLink, fromTestBank }) => (
  <>
    {previewLink ? (
      <a
        href={previewLink}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline-offset-4 hover:underline"
      >
        {label}
      </a>
    ) : (
      label
    )}
    {fromTestBank && (
      <span className="block text-xs font-normal text-gray-500">
        (From Assessments Library)
      </span>
    )}
  </>
);

// Toggle-able action buttons: a darker shade indicates the selected state.
const REMOVE_BUTTON = "bg-red-600 text-white hover:bg-[#AC1A1A]";
const REMOVE_SELECTED = "bg-[#AC1A1A] hover:bg-[#AC1A1A]";
const ASSIGN_BUTTON = "bg-sky-500 text-white hover:bg-[#0E85BC]";
const ASSIGN_SELECTED = "bg-[#0E85BC] hover:bg-[#0E85BC]";

// Review-step panel: same shell and table layout as the edit-step sections,
// without the per-row action column.
const ReviewSection = ({ title, items }) => (
  <section className="space-y-3 rounded-lg border border-slate-300 bg-surface p-6">
    <h3 className="font-inter text-base font-semibold text-slate-700">
      {title}
    </h3>

    <div className="overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="sticky top-0 z-10">
            <TableHead className="w-full">Title</TableHead>
            <TableHead className="whitespace-nowrap text-left">
              Status
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="w-full bg-white font-medium">
                <AssessmentTitle
                  label={item.label}
                  previewLink={item.previewLink}
                  fromTestBank={item.fromTestBank}
                />
              </TableCell>
              <TableCell className="whitespace-nowrap bg-white text-left">
                <StatusIndicator status={item.status} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </section>
);

const idSetsEqual = (a, b) =>
  a.size === b.size && [...a].every((id) => b.has(id));

const SEARCH_RESULTS_PAGE_SIZE = 5;

// The "Job Assessments" tab: review/remove current assessments and assign new
// ones on one screen, then confirm the pending changes. It fetches its own data
// when it mounts (i.e. when the dialog opens on this tab) and closes the
// dialog through the `onClose` callback supplied by the parent.
const JobAssessmentsTab = ({ onClose }) => {
  const { jobOpp, updateJob, updateJobLocally } = useContext(JobsContext);
  const { addGraphQLAlert, addCustomAlert } = useContext(AlertContext);

  // Steps: edit (current + assign) -> review.
  const [step, setStep] = useState("edit");
  const [searchTerm, setSearchTerm] = useState("");
  const [includeTestBank, setIncludeTestBank] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchResultsPage, setSearchResultsPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Pending actions the user has toggled (not yet saved):
  //  - current problems marked for removal (ids)
  //  - newly assigned problems (full objects, keyed by id)
  const [pendingRemovalIds, setPendingRemovalIds] = useState(() => new Set());
  const [addedProblemsById, setAddedProblemsById] = useState({});

  // Guards against out-of-order async search responses.
  const searchReqRef = useRef(0);
  // Debounce timer so typing doesn't fire a backend request per keystroke.
  const searchDebounceRef = useRef(null);
  // Scroll container for the edit step (results sit below; bring them into view).
  const editScrollRef = useRef(null);
  // Pin the search bar to the top of the scroll area when results arrive.
  const assignSearchBarRef = useRef(null);

  const SEARCH_DEBOUNCE_MS = 300;

  // Cancel a pending debounced search if the tab unmounts (e.g. the dialog is
  // closed) so the timer doesn't fire and set state on an unmounted component.
  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  // When assign search finishes, scroll so the search bar sits at the top of the
  // panel (results or empty-state stay visible below).
  useEffect(() => {
    if (step !== "edit" || isSearching || !searchTerm.trim()) {
      return;
    }

    const container = editScrollRef.current;
    const searchBar = assignSearchBarRef.current;
    if (!container || !searchBar) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      const containerTop = container.getBoundingClientRect().top;
      const searchBarTop = searchBar.getBoundingClientRect().top;
      const nextTop = container.scrollTop + (searchBarTop - containerTop);

      container.scrollTo({ top: nextTop, behavior: "smooth" });
    });

    return () => cancelAnimationFrame(frameId);
  }, [step, isSearching, searchTerm, searchResults]);

  const [
    fetchAssessments,
    { data: assessmentsData, loading: assessmentsLoading },
  ] = useLazyQuery(GET_JOB_OPPORTUNITY_ASSESSMENTS, {
    fetchPolicy: "network-only",
    onError: (error) => addGraphQLAlert({ errors: error?.graphQLErrors }),
  });

  const [searchProblems] = useLazyQuery(SEARCH_ROPES_PROBLEMS, {
    fetchPolicy: "network-only",
    onError: (error) => addGraphQLAlert({ errors: error?.graphQLErrors }),
  });

  // Fetch the job's current assessments once, when the tab first mounts.
  const hasFetchedRef = useRef(false);
  if (!hasFetchedRef.current && jobOpp?.id) {
    hasFetchedRef.current = true;
    fetchAssessments({ variables: { id: jobOpp.id } });
  }

  // Names/links for the job's assessments, keyed by problem id.
  const assessmentsById = useMemo(() => {
    const result = assessmentsData?.getJobOpportunityAssessments;
    const list = Array.isArray(result) ? result : result ? [result] : [];

    return list.reduce((acc, assessment) => {
      acc[assessment.id] = assessment;
      return acc;
    }, {});
  }, [assessmentsData]);

  // The job's current problems come from `ropesProblems` (real id + status),
  // enriched with the assessment name where available.
  const currentProblems = useMemo(
    () =>
      (jobOpp?.ropesProblems || []).map((problem) => ({
        id: problem.id,
        status: problem.status,
        name: assessmentsById[problem.id]?.name || problem.id,
        previewLink: assessmentsById[problem.id]?.previewLink,
      })),
    [jobOpp?.ropesProblems, assessmentsById]
  );

  const assignedProblemIds = useMemo(
    () => new Set((jobOpp?.ropesProblems || []).map((problem) => problem.id)),
    [jobOpp?.ropesProblems]
  );

  const problemsToRemove = useMemo(
    () =>
      currentProblems.filter((problem) => pendingRemovalIds.has(problem.id)),
    [currentProblems, pendingRemovalIds]
  );

  const problemsToAdd = useMemo(
    () => Object.values(addedProblemsById),
    [addedProblemsById]
  );

  // Final review list: assessments being removed listed first, then the newly
  // assigned ones. Normalised to a common shape so one table can render both.
  const reviewItems = useMemo(
    () => [
      ...problemsToRemove.map((problem) => ({
        id: problem.id,
        label: problem.name,
        previewLink: problem.previewLink,
        action: "remove",
      })),
      ...problemsToAdd.map((problem) => ({
        id: problem.id,
        label: problem.title,
        previewLink: problem.previewLink,
        fromTestBank: problem.isFromRopesTestBank,
        action: "add",
      })),
    ],
    [problemsToRemove, problemsToAdd]
  );

  // The resulting set of problem ids: current problems that weren't removed,
  // plus newly assigned problems.
  const finalProblemIds = useMemo(() => {
    const ids = new Set();

    currentProblems.forEach((problem) => {
      if (!pendingRemovalIds.has(problem.id)) {
        ids.add(problem.id);
      }
    });

    Object.keys(addedProblemsById).forEach((id) => ids.add(id));

    return ids;
  }, [currentProblems, pendingRemovalIds, addedProblemsById]);

  const hasChanges = !idSetsEqual(finalProblemIds, assignedProblemIds);

  const pagedSearchResults = useMemo(() => {
    const start = (searchResultsPage - 1) * SEARCH_RESULTS_PAGE_SIZE;

    return searchResults.slice(start, start + SEARCH_RESULTS_PAGE_SIZE);
  }, [searchResults, searchResultsPage]);

  const runSearch = async (term, withTestBank) => {
    if (!term.trim()) {
      // Bump the counter so any in-flight search is superseded and its (stale)
      // results don't repopulate the cleared list when it resolves.
      searchReqRef.current += 1;
      setSearchResults([]);
      setSearchResultsPage(1);
      setIsSearching(false);
      return;
    }

    const reqId = ++searchReqRef.current;
    setIsSearching(true);

    const { data } = await searchProblems({
      variables: { query: term, hideRopesTestBank: !withTestBank },
    });

    // Ignore responses for a superseded search.
    if (searchReqRef.current !== reqId) {
      return;
    }

    setSearchResults(data?.searchRopesProblems || []);
    setSearchResultsPage(1);
    setIsSearching(false);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    clearTimeout(searchDebounceRef.current);

    // Clearing the input should empty the list right away (no backend hit);
    // typing is debounced to avoid a request per keystroke.
    if (!value.trim()) {
      runSearch(value, includeTestBank);
      return;
    }

    searchDebounceRef.current = setTimeout(() => {
      runSearch(value, includeTestBank);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleIncludeTestBankChange = (checked) => {
    const nextChecked = checked === true;
    setIncludeTestBank(nextChecked);

    // Toggling is a deliberate single action - search immediately.
    clearTimeout(searchDebounceRef.current);
    runSearch(searchTerm, nextChecked);
  };

  const handleToggleRemove = (problemId) => {
    setPendingRemovalIds((prev) => {
      const next = new Set(prev);

      if (next.has(problemId)) {
        next.delete(problemId);
      } else {
        next.add(problemId);
      }

      return next;
    });
  };

  const handleToggleAssign = (problem) => {
    setAddedProblemsById((prev) => {
      const next = { ...prev };

      if (next[problem.id]) {
        delete next[problem.id];
      } else {
        next[problem.id] = problem;
      }

      return next;
    });
  };

  const handleSave = async () => {
    const ids = [...finalProblemIds];

    // Status by id, for keeping local job state in sync after the update.
    const statusById = {};
    currentProblems.forEach((problem) => {
      statusById[problem.id] = problem.status;
    });
    Object.values(addedProblemsById).forEach((problem) => {
      statusById[problem.id] = problem.status;
    });

    try {
      setIsSaving(true);

      await updateJob(jobOpp.id, {
        ropesProblems: ids.map((id) => ({ id })),
      });

      await updateJobLocally(
        ids.map((id) => ({ id, status: statusById[id] })),
        "ropesProblems"
      );

      addCustomAlert({
        title: "Assessments updated",
        variation: "success",
        message: "Updated assignments for the job",
        duration: 3000,
      });

      onClose();
    } catch (error) {
      addGraphQLAlert({ errors: error?.errors });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4">
      {step === "edit" ? (
        <>
          <p className="mt-3 font-inter text-xs font-normal text-slate-700">
            Review the job's current assessments or assign a new one.
          </p>

          <div
            ref={editScrollRef}
            className="min-h-0 flex-1 space-y-6 overflow-y-auto p-1"
          >
            <section className="space-y-3 rounded-lg border border-slate-300 bg-surface p-6">
              <h3 className="font-inter text-base font-semibold text-slate-700">
                Current assessments
              </h3>

              {assessmentsLoading ? (
                <div className="flex items-center gap-x-2 text-sm text-gray-500">
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                  Loading assessments...
                </div>
              ) : currentProblems.length > 0 ? (
                <div className="overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="sticky top-0 z-10">
                        <TableHead className="w-full">Title</TableHead>
                        <TableHead className="whitespace-nowrap text-left">
                          Status
                        </TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentProblems.map((problem) => {
                        const isRemoved = pendingRemovalIds.has(problem.id);

                        return (
                          <TableRow key={problem.id}>
                            <TableCell className="w-full font-medium bg-white">
                              <AssessmentTitle
                                label={problem.name}
                                previewLink={problem.previewLink}
                              />
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-left bg-white">
                              <StatusIndicator status={problem.status} />
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right bg-white">
                              <Button
                                onClick={() => handleToggleRemove(problem.id)}
                                className={cn(
                                  "min-w-[6.5rem]",
                                  REMOVE_BUTTON,
                                  isRemoved && REMOVE_SELECTED
                                )}
                              >
                                {isRemoved ? "Selected" : "Remove"}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  No assessments assigned to this job.
                </p>
              )}
            </section>

            <section className="space-y-3 rounded-lg border border-slate-300 bg-surface p-6">
              <h3 className="font-inter text-base font-semibold text-slate-700">
                Assign an assessment
              </h3>

              <div
                ref={assignSearchBarRef}
                className="flex items-center gap-x-4"
              >
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Type to search for an assessment"
                    className="bg-white pl-9"
                  />
                </div>

                <label
                  htmlFor="include-test-bank"
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm"
                >
                  <Checkbox
                    id="include-test-bank"
                    checked={includeTestBank}
                    onCheckedChange={(checked) =>
                      handleIncludeTestBankChange(!!checked)
                    }
                  />
                  Include Assessment Library
                </label>
              </div>

              {isSearching ? (
                <div className="flex items-center gap-x-2 text-sm text-gray-500">
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                  Searching...
                </div>
              ) : searchResults.length > 0 ? (
                <div className="overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="sticky top-0 z-10">
                        <TableHead className="w-full">Title</TableHead>
                        <TableHead className="whitespace-nowrap text-left">
                          Status
                        </TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedSearchResults.map((problem) => {
                        const isAdded = !!addedProblemsById[problem.id];

                        return (
                          <TableRow key={problem.id}>
                            <TableCell className="w-full bg-white font-medium">
                              <AssessmentTitle
                                label={problem.title}
                                previewLink={problem.previewLink}
                                fromTestBank={problem.isFromRopesTestBank}
                              />
                            </TableCell>
                            <TableCell className="whitespace-nowrap bg-white text-left">
                              <StatusIndicator status={problem.status} />
                            </TableCell>
                            <TableCell className="whitespace-nowrap bg-white text-right">
                              {assignedProblemIds.has(problem.id) ? (
                                <span className="block text-center text-sm text-gray-500">
                                  Already
                                  <br />
                                  Assigned
                                </span>
                              ) : (
                                <Button
                                  onClick={() => handleToggleAssign(problem)}
                                  className={cn(
                                    "min-w-[6.5rem]",
                                    ASSIGN_BUTTON,
                                    isAdded && ASSIGN_SELECTED
                                  )}
                                >
                                  {isAdded ? "Selected" : "Assign"}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  <Paginator
                    className="mt-3"
                    totalItems={searchResults.length}
                    pageSize={SEARCH_RESULTS_PAGE_SIZE}
                    page={searchResultsPage}
                    onPageChange={setSearchResultsPage}
                  />
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  {searchTerm.trim()
                    ? "No assessments found."
                    : "Your search results will appear here when you type something."}
                </p>
              )}
            </section>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={() => setStep("review")}
              className={ASSIGN_BUTTON}
            >
              Next
            </Button>
          </DialogFooter>
        </>
      ) : (
        <>
          <p className="mt-3 font-inter text-lg font-semibold text-slate-700">
            Review your changes and confirm
          </p>

          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-1">
            {problemsToRemove.length > 0 && (
              <ReviewSection
                title="Assessments to remove"
                items={problemsToRemove.map((problem) => ({
                  id: problem.id,
                  label: problem.name,
                  previewLink: problem.previewLink,
                  status: problem.status,
                }))}
              />
            )}

            {problemsToAdd.length > 0 && (
              <ReviewSection
                title="Assessments to add"
                items={problemsToAdd.map((problem) => ({
                  id: problem.id,
                  label: problem.title,
                  previewLink: problem.previewLink,
                  fromTestBank: problem.isFromRopesTestBank,
                  status: problem.status,
                }))}
              />
            )}

            {reviewItems.length === 0 && (
              <p className="text-sm text-gray-500">
                No changes to apply. Go back to remove or assign assessments.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStep("edit")}>
              Back
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className={ASSIGN_BUTTON}
            >
              {isSaving && (
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirm
            </Button>
          </DialogFooter>
        </>
      )}
    </div>
  );
};

export default JobAssessmentsTab;
